<script lang="ts">
    import './NativeHtmlEmbedContent.css';

    import { page } from '$app/stores';
    import { IFrameMessageType } from '$lib/components/Blocks/_utils/types';
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import { htmlEmbedFirstInteraction } from '$lib/global/tracking/v4';
    import { cn } from '$lib/utils/cn';

    import unescape from 'lodash/unescape';
    import { onDestroy, onMount } from 'svelte';

    import { injectHtml } from '../helpers';

    // Please also check out "Notes.md" for more details on "first interaction detection".

    export let head = '';
    export let body = '';
    export let enforceCenteredCustomHtmlContent = '';
    export let campaignId = '';
    export let firstResultPage = '';
    export let nextPage = '';

    const {
        data: { pageIdSlugMap },
    } = $page;

    const nextPageNavigator = new NextPageNavigator(campaignId, firstResultPage, pageIdSlugMap);

    let wrapperElement: HTMLDivElement;

    const headHtmlToInject = unescape(head);
    const bodyHtmlToInject = unescape(body);

    const handleIframeMessage = (event: MessageEvent) => {
        const { type, data } = event.data;

        if (!type) {
            return;
        }

        switch (type) {
            case IFrameMessageType.navigateToNextPage:
                nextPageNavigator.go(nextPage);
                break;
            case IFrameMessageType.navigateToPageBySlug:
                if (data?.pageSlug) {
                    nextPageNavigator.go(data.pageSlug);
                }
                break;
        }
    };

    // First interaction detection
    onMount(() => {
        let activeElementCheckInterval: ReturnType<typeof setInterval> | undefined = undefined;
        let removeClickListener: null | (() => void) = null;

        const firstInteractionDetected = () => {
            clearTimeout(activeElementCheckInterval);
            removeClickListener?.();

            htmlEmbedFirstInteraction({ embedType: 'native' });
        };

        activeElementCheckInterval = setInterval(() => {
            const activeElement = document.activeElement;

            // Cheap checks first.
            if (
                activeElement &&
                activeElement !== wrapperElement &&
                activeElement.tagName === 'IFRAME' &&
                wrapperElement.contains(activeElement)
            ) {
                firstInteractionDetected();
            }
        }, 333);

        const clickListener = (event: MouseEvent) => {
            if (event.target !== wrapperElement && wrapperElement.contains(event.target as Node)) {
                firstInteractionDetected();
            }
        };

        wrapperElement.addEventListener('click', clickListener, true);
        removeClickListener = () => {
            wrapperElement.removeEventListener('click', clickListener, true);
        };
    });

    // Code injection
    onMount(() => {
        // I also tested a Shadow DOM for style encapsulation, but it
        // doesn't work for OpenTable and HubSpot.
        //
        // Observations regarding injecting into a Shadow DOM:
        // - Injected scripts are loaded by the browser.
        // - Scripts with inline code are executed.

        injectHtml(headHtmlToInject, document.head);
        injectHtml(bodyHtmlToInject, wrapperElement);
    });

    // Enable page navigation
    onMount(() => {
        window.perspective = {
            ...window.perspective,
            navigateToNextPage: () => {
                nextPageNavigator.go(nextPage);
            },
            navigateToResultPage: () => {
                nextPageNavigator.go('result');
            },
            navigateToPageBySlug: (pageSlug: string) => {
                nextPageNavigator.go(pageSlug);
            },
        };

        // We use this event listener to handle messages from the iframe
        // Currently only used for navigating between pages (part of Marketing self-serve checkout project)
        window.addEventListener('message', handleIframeMessage);
    });

    // Remove navigation methods again
    onDestroy(() => {
        delete window.perspective?.navigateToNextPage;
        delete window.perspective?.navigateToResultPage;
        delete window.perspective?.navigateToPageBySlug;

        window.removeEventListener('message', handleIframeMessage);
    });

    const wrapperClass = cn(
        'nativeHtmlEmbedContentWrapper fun-w-full max-sm:fun-rounded-none',
        getBorderRadius('embed'),
        {
            'fun-flex fun-flex-col fun-items-center fun-justify-center':
                enforceCenteredCustomHtmlContent,
        },
    );
</script>

<div bind:this={wrapperElement} class={wrapperClass}></div>
